/**
 * The one answer to "what does this room's calendar say for this month?".
 *
 * WHY THIS FILE EXISTS. Until S4.1 the question was answered twice — once in
 * `components/RoomCalendar.js` for the unit detail page, once in
 * `pages/admin/rooms/occupancyCalendarModel.js` for the Co-Living matrix — by
 * two verbatim copies of the same logic. Duplication is tolerable for layout.
 * It is not tolerable here, because the copies decide whether a room reads as
 * occupied or free, and the S4.1 discovery found both copies wrong in the same
 * way. Fixing one and not the other would have been the next incident, so the
 * answer now lives in exactly one place and both consumers import it.
 *
 * THE BUG THIS FIXES. The old selection excluded only `ended`, so a CANCELLED
 * tenancy stayed eligible to supply a room's calendar — and because a
 * cancellation is only permitted at or just before move-in, cancelled rows
 * carry recent or future move-in dates, which is exactly what wins the
 * newest-move-in tiebreak. A cancelled row could therefore out-rank the live
 * one and paint an occupied room free for every month, while contributing
 * nothing to the totals that would otherwise have exposed it.
 *
 * WHAT `cancelled` MEANS, and why it is not a calendar state. A cancelled
 * tenancy did not end — it never started. The cancellation guard
 * (`tenancy_lifecycle._derive_cancelability`) only permits a cancellation
 * before move-in or inside the three-day no-show grace, and the write touches
 * no date and releases no room. Every other layer in the system already draws
 * the same conclusion: the partial EXCLUDE constraint (migration 111), the
 * Python overlap pre-check, `tenancy_state.effective_tenancy_status`,
 * `occupancy_service.compute_room_planning` and this repo's own
 * `unitOccupancyStatus.effectiveTenancyStatus` all treat it as non-occupying.
 * `docs/KPI_SEMANTICS.md` §E.4 — the binding contract for this calendar —
 * names seven states and `cancelled` is not among them.
 *
 * THE SEVENTH STATE THE CONTRACT NAMES AND THE CODE NEVER BUILT is
 * `free_unknown`, "Frei – Unbekannt": free, with no contract end to explain
 * why. It is distinct from `free_planned`, which is free BECAUSE a known
 * contract end says so. The old code had one word, "Frei", doing both jobs.
 *
 * `unknown` IS NOT AN EIGHTH BUSINESS STATE. It means this layer could not
 * establish an authoritative answer — a relevant tenancy exists but its
 * lifecycle data is missing, empty, short or unrecognised. Rendering that as
 * "Frei" is how a broken payload hides behind a plausible word, so it fails
 * closed instead: its own neutral word, its own family, and no contribution to
 * any count or to the potential loss.
 *
 * PURE LEAF. No React, no fetch, no UI. The only import is this repo's own
 * Swiss-calendar helper, which is itself dependency-free — duplicating a
 * timezone rule here would be the very mistake this file exists to undo.
 */
import { currentBusinessPeriod } from "./datetime";

/** Months the backend actually supplies per tenancy (`build_lifecycle_calendar`). */
export const MAX_MONTH_COUNT = 12;

/** Months shown unless the period control says otherwise. */
export const DEFAULT_MONTH_COUNT = 9;

/** The period options a consumer may offer. */
export const MONTH_COUNT_OPTIONS = [6, 9, 12];

/**
 * The technical fail-closed state. Deliberately not a member of
 * `LIFECYCLE_STATES` — it describes the DATA, never the room.
 */
export const UNKNOWN_STATE = "unknown";

/**
 * The states `tenancy_lifecycle.lifecycle_calendar_status` may emit for a
 * tenancy this layer is allowed to read.
 *
 * `cancelled` is deliberately absent. It is a real backend emission, but a
 * cancelled tenancy is never selected (see `isCalendarEligibleTenancy`), so a
 * `cancelled` row reaching this layer means an assumption broke — and it then
 * resolves to `unknown` rather than being quietly given a word.
 */
export const BACKEND_CALENDAR_STATES = Object.freeze([
  "secure",
  "cancellable",
  "notice_given",
  "always_cancellable",
  "reserved",
  "free_planned",
]);

/** The visible business vocabulary: the six above plus the no-tenancy answer. */
export const LIFECYCLE_STATES = Object.freeze([
  ...BACKEND_CALENDAR_STATES,
  "free_unknown",
]);

/**
 * German words.
 *
 * The six backend states keep the wording that was already on screen.
 * `free_unknown` takes its label verbatim from `docs/KPI_SEMANTICS.md` §E.4
 * rather than being invented here.
 */
export const LIFECYCLE_LABELS = {
  secure: "Sicher",
  cancellable: "Kündbar",
  notice_given: "Gekündigt",
  always_cancellable: "Jederzeit kündbar",
  reserved: "Reserviert",
  free_planned: "Frei (geplant)",
  free_unknown: "Frei – Unbekannt",
  [UNKNOWN_STATE]: "Unbekannt",
};

/** Visual family. `unknown` gets its own so it can never pass as free. */
export const LIFECYCLE_FAMILY = {
  secure: "occupied",
  cancellable: "occupied",
  notice_given: "occupied",
  always_cancellable: "occupied",
  reserved: "reserved",
  free_planned: "free",
  free_unknown: "free",
  [UNKNOWN_STATE]: UNKNOWN_STATE,
};

/** Family headings. */
export const FAMILY_LABELS = {
  occupied: "Belegt",
  reserved: "Reserviert",
  free: "Frei",
  [UNKNOWN_STATE]: "Datenstatus",
};

/** The business legend: four occupied, reserved, two free. Never `unknown`. */
export const LEGEND_GROUPS = [
  {
    family: "occupied",
    states: ["secure", "cancellable", "notice_given", "always_cancellable"],
  },
  { family: "reserved", states: ["reserved"] },
  { family: "free", states: ["free_planned", "free_unknown"] },
];

/**
 * Why a room is `free_planned` — the economic half of that state.
 *
 * The state says the room is empty; these say what it cost. Both arrive from
 * the backend on the row itself (`free_reason`), because only the backend can
 * see which of the two dates survived on the tenancy: a termination replaces
 * the planned end inside `move_out_date`, so from the payload's state alone the
 * two are indistinguishable.
 *
 * `FREE_REASON_UNKNOWN` is the fail-closed answer, and it also covers a
 * `free_planned` row that carries NO reason at all — an older backend, mid
 * deploy. Treating that as "no loss" would quietly zero a real one.
 */
export const FREE_REASON_CONTRACT_END = "contract_end";
export const FREE_REASON_TERMINATION = "termination";
export const FREE_REASON_UNKNOWN = "unknown";

export const FREE_PLANNED_REASONS = Object.freeze([
  FREE_REASON_CONTRACT_END,
  FREE_REASON_TERMINATION,
  FREE_REASON_UNKNOWN,
]);

/** Shown beside an aggregate that could not be completed. */
export const INCOMPLETE_VALUE_LABEL = "—";

/** The accessible qualifier that goes with it. */
export const INCOMPLETE_HINT = "Daten unvollständig";

/** The page-level notice when any cell in a matrix is unknown. */
export const DATA_QUALITY_NOTICE =
  "Für einzelne Kalendermonate fehlen eindeutige Belegungsdaten.";

/* ------------------------------------------------------------------ *
 * Tenancy selection
 * ------------------------------------------------------------------ */

/**
 * Statuses that disqualify a tenancy from supplying a room's calendar.
 *
 * `ended` was always here. `cancelled` is the S4.1 correction: it is the
 * status that means the tenancy never started, and a tenancy that never
 * started has nothing to say about who is in the room.
 */
export const INELIGIBLE_CALENDAR_STATUSES = Object.freeze(["ended", "cancelled"]);

/** The DB/API status string, whether it arrives as a string or an enum object. */
export function tenancyStatusRaw(t) {
  const st = t?.status;
  if (st == null) return "";
  if (typeof st === "object" && st.value != null) return String(st.value);
  return String(st);
}

/** May this tenancy supply a room's calendar? */
export function isCalendarEligibleTenancy(t) {
  if (!t) return false;
  return !INELIGIBLE_CALENDAR_STATUSES.includes(
    tenancyStatusRaw(t).trim().toLowerCase()
  );
}

function roomIdOf(room) {
  return String(room?.room_id || room?.roomId || room?.id || "");
}

function tenancyRoomIdOf(t) {
  return String(t?.room_id || t?.roomId || "");
}

/**
 * The tenancy whose calendar a room shows.
 *
 * Same room, eligible status, newest move-in when several qualify. The
 * tiebreak is unchanged from before S4.1 on purpose — it was never the
 * problem; the pool it chose from was. Never mutates the input array.
 */
export function pickRelevantCalendarTenancyForRoom(room, tenancies) {
  if (!Array.isArray(tenancies)) return null;
  const rid = roomIdOf(room);
  const candidates = tenancies.filter(
    (t) => tenancyRoomIdOf(t) === rid && isCalendarEligibleTenancy(t)
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  return candidates.reduce((best, cur) => {
    const sb = String(best.move_in_date || "").slice(0, 10);
    const sc = String(cur.move_in_date || "").slice(0, 10);
    return sc > sb ? cur : best;
  });
}

/* ------------------------------------------------------------------ *
 * The month axis
 * ------------------------------------------------------------------ */

const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Is this a well-formed "YYYY-MM" key? */
export function isMonthKey(value) {
  return typeof value === "string" && MONTH_KEY_RE.test(value);
}

/** `{year, month}` (month 1-based) → "YYYY-MM". */
export function monthKeyOf(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

/**
 * Move a month key forward or back.
 *
 * Pure string/number arithmetic. No `Date` is involved, so no timezone can
 * push the answer into the neighbouring month.
 */
export function addMonthsToKey(key, offset) {
  const [y, m] = key.split("-").map(Number);
  const zero = y * 12 + (m - 1) + offset;
  return monthKeyOf(Math.floor(zero / 12), (zero % 12) + 1);
}

/**
 * "Okt 26" for a month key.
 *
 * The `Date` is built from the key's own numbers and only ever formatted, so
 * this names the month it was given in every timezone.
 */
export function monthLabelForKey(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("de-CH", {
    month: "short",
    year: "2-digit",
  });
}

/**
 * The month keys a tenancy's calendar authoritatively covers, or `null`.
 *
 * "Authoritative" means well formed: a non-empty array in which every row
 * carries a valid month key. A half-built calendar is not a weaker source of
 * truth, it is not a source of truth.
 */
export function authoritativeMonthKeysOf(tenancy) {
  const cal = tenancy?.lifecycle_calendar;
  if (!Array.isArray(cal) || cal.length === 0) return null;
  const keys = cal.map((row) => (row ? String(row.month) : ""));
  if (!keys.every(isMonthKey)) return null;
  return keys;
}

/**
 * Which month does the authoritative window start on?
 *
 * EVERY tenancy is sampled here, including the ineligible ones. The anchor is
 * a TECHNICAL question — "which twelve months did the server answer for?" —
 * and the server answers it identically for every row of one response, since
 * `build_lifecycle_calendar` anchors them all on the same Swiss month. Reading
 * a cancelled row's month KEYS is therefore not the same as reading its
 * status, and sampling widely means a page whose only well-formed calendar
 * happens to sit on an ineligible row still gets the right window.
 *
 * The anchor is the EARLIEST key rather than the first element, so an
 * out-of-order payload cannot shift the window.
 *
 * Returns `{ anchorKey, source, consistent, anchors }`. `anchors` is every
 * distinct anchor seen, so a caller can say how the payload disagreed rather
 * than only that it did.
 */
export function resolveMonthAxisAnchor(tenancies, now) {
  const counts = new Map();
  if (Array.isArray(tenancies)) {
    for (const t of tenancies) {
      const keys = authoritativeMonthKeysOf(t);
      if (!keys) continue;
      const anchor = keys.reduce((a, b) => (b < a ? b : a));
      counts.set(anchor, (counts.get(anchor) || 0) + 1);
    }
  }

  if (counts.size === 0) {
    // Nothing authoritative anywhere — the only remaining honest anchor is the
    // SWISS business month. Never the device's: east of Zurich the device can
    // already be in next month while the business day has not turned.
    const { year, month } = currentBusinessPeriod(now);
    return {
      anchorKey: monthKeyOf(year, month),
      source: "clock",
      consistent: true,
      anchors: [],
    };
  }

  const anchors = Array.from(counts.keys()).sort();
  // Most frequent wins; the earliest breaks a tie. Deterministic either way —
  // an axis that depended on array order would make the same payload render
  // two different windows.
  const anchorKey = anchors.reduce((best, cur) =>
    counts.get(cur) > counts.get(best) ? cur : best
  );

  return {
    anchorKey,
    source: "payload",
    consistent: anchors.length === 1,
    anchors,
  };
}

/**
 * The visible month columns.
 *
 * Anchored on the payload whenever the payload has an answer, on the Swiss
 * business month otherwise — never on the device's calendar month. Index 0 is
 * the anchor, which the backend builds as the current Swiss month, so it is
 * the current month by construction rather than by a second date rule here.
 *
 * Months beyond what a given tenancy's calendar covers are NOT invented: the
 * axis asks for them, and `resolveCalendarMonthTruth` answers `unknown`.
 */
export function resolveMonthAxis({
  tenancies = null,
  monthCount = DEFAULT_MONTH_COUNT,
  now = undefined,
} = {}) {
  const n = Math.max(0, Math.min(MAX_MONTH_COUNT, Number(monthCount) || 0));
  const anchor = resolveMonthAxisAnchor(tenancies, now);
  const months = Array.from({ length: n }, (_, i) => {
    const key = addMonthsToKey(anchor.anchorKey, i);
    return { key, label: monthLabelForKey(key), isCurrent: i === 0 };
  });
  return { months, ...anchor };
}

/* ------------------------------------------------------------------ *
 * Transitions (R8 / F3-D)
 * ------------------------------------------------------------------ */

/**
 * The accessible heading for a cell's transitions.
 *
 * One word for both surfaces, so the unit-detail calendar and the Co-Living
 * matrix cannot describe the same payload differently.
 */
export const TRANSITION_HINT = "Wechsel im Monat";

/** The visible marker. A glyph, not a colour — see the note on the indicator. */
export const TRANSITION_MARKER = "→";

const TRANSITION_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * One transition, or `null` when it cannot be trusted.
 *
 * FAIL-CLOSED, on the same rule the state vocabulary already follows. A month
 * cell is allowed to say less than the payload offered; it is not allowed to
 * say something it cannot stand behind. So anything unreadable — a missing
 * date, a date that is not a plain `YYYY-MM-DD`, a state this layer does not
 * know, or a "change" from a state to itself — is DROPPED rather than rendered
 * with a hole in it.
 *
 * THE STATE ALLOWLIST IS THE EXISTING ONE. `BACKEND_CALENDAR_STATES` already
 * says which words the backend may send, and a transition is made of exactly
 * those words. A second list here would be a second place to forget, and the
 * first divergence between them would be invisible: both would still render.
 * `cancelled` is absent from it on purpose (see the note there) and is
 * therefore rejected here too — which matches the backend, where a cancelled
 * tenancy produces no transitions at all.
 */
function normaliseTransition(raw) {
  if (!raw || typeof raw !== "object") return null;
  const date = String(raw.date == null ? "" : raw.date).trim();
  if (!TRANSITION_DATE_RE.test(date)) return null;
  const from = String(raw.from_status == null ? "" : raw.from_status)
    .trim()
    .toLowerCase();
  const to = String(raw.to_status == null ? "" : raw.to_status)
    .trim()
    .toLowerCase();
  if (!BACKEND_CALENDAR_STATES.includes(from)) return null;
  if (!BACKEND_CALENDAR_STATES.includes(to)) return null;
  if (from === to) return null;
  return { date, fromState: from, toState: to };
}

/**
 * A cell's transitions: validated, de-duplicated and in date order.
 *
 * A LIST, NEVER A FLAG (the F3-D decision). A month can hold several changes —
 * somebody moves in on the 5th and a contract ends on the 20th — and a boolean
 * could describe neither of them, while a single slot would silently keep one
 * and lose the other.
 *
 * Sorted here as well as by the backend. Not distrust: the sort is two lines
 * and makes the render order a property of THIS layer, so a cell reads the same
 * whatever order it was handed. The backend's own ordering is pinned on its own
 * side (`test_r8_6`), so a regression there still surfaces as a red test rather
 * than being quietly tidied away here.
 */
export function normaliseTransitions(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const tr = normaliseTransition(item);
    if (!tr) continue;
    const key = `${tr.date}|${tr.fromState}|${tr.toState}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tr);
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * "17.09.2026" — Swiss day-first, from the string's own digits.
 *
 * No `Date` is constructed, so no timezone can move the day. The same reason
 * `addMonthsToKey` above does its arithmetic on numbers.
 */
export function formatTransitionDate(iso) {
  const [y, m, d] = String(iso).split("-");
  return `${d}.${m}.${y}`;
}

/** "17.09.2026 · Reserviert → Sicher". Labels from the existing map, never a new one. */
export function describeTransition(tr) {
  return `${formatTransitionDate(tr.date)} · ${LIFECYCLE_LABELS[tr.fromState]} → ${
    LIFECYCLE_LABELS[tr.toState]
  }`;
}

/**
 * The whole accessible sentence for a cell, or `""` when nothing happened.
 *
 * THE SMALLEST SHARED PRESENTATION UTILITY (§20). The two calendars do not
 * share a cell renderer — one is a dark unit-detail grid, the other a table in
 * the Co-Living matrix — and R8 is not the slice that merges them. What they
 * must share is the WORDING, so both import this rather than each phrasing the
 * same payload in its own way.
 */
export function describeTransitions(transitions) {
  const list = transitions || [];
  if (list.length === 0) return "";
  return `${TRANSITION_HINT}: ${list.map(describeTransition).join(", ")}`;
}

/* ------------------------------------------------------------------ *
 * The month truth
 * ------------------------------------------------------------------ */

function truth(state, freeReason = null, transitions = []) {
  return {
    state,
    family: LIFECYCLE_FAMILY[state],
    label: LIFECYCLE_LABELS[state],
    freeReason,
    // R8: always present, empty when nothing happens. A cell whose SHAPE
    // depended on whether something happened would force every consumer into a
    // branch, and the forgotten branch reads exactly like "no transitions".
    transitions,
  };
}

/**
 * The reason a `free_planned` row carries, normalised.
 *
 * Anything this layer does not recognise — absent, null, a word from a newer
 * backend — becomes `unknown`, which withholds a figure rather than guessing
 * one. Same fail-closed rule the state vocabulary already follows.
 */
function normaliseFreeReason(raw) {
  const value = String(raw == null ? "" : raw).trim().toLowerCase();
  return FREE_PLANNED_REASONS.includes(value) ? value : FREE_REASON_UNKNOWN;
}

/**
 * What one month of one room says.
 *
 * Two questions, deliberately kept apart:
 *
 *   NO relevant tenancy            → `free_unknown`. Nobody has a claim on the
 *                                    room and nothing explains why, which is
 *                                    precisely what the contract's
 *                                    "Frei – Unbekannt" describes. A room whose
 *                                    only tenancies were ended or cancelled
 *                                    lands here, and so does one with none at
 *                                    all — the backend's own occupancy
 *                                    classification calls both free.
 *
 *   A relevant tenancy with data
 *   this layer cannot read         → `unknown`. The serializer guarantees a
 *                                    gapless twelve-month calendar with
 *                                    non-null statuses, so every one of these
 *                                    branches means the guarantee broke. "Frei"
 *                                    would be a guess, and a confident one.
 */
export function resolveCalendarMonthTruth({ tenancy, monthKey }) {
  if (!tenancy) return truth("free_unknown");

  const cal = tenancy.lifecycle_calendar;
  if (!Array.isArray(cal) || cal.length === 0) return truth(UNKNOWN_STATE);

  const key = String(monthKey || "");
  const row = cal.find((x) => x && String(x.month) === key);
  if (!row || row.status == null) return truth(UNKNOWN_STATE);

  const state = String(row.status).trim().toLowerCase();
  if (!BACKEND_CALENDAR_STATES.includes(state)) return truth(UNKNOWN_STATE);
  // R8 — read only once the STATE is known to be authoritative. A row this
  // layer already refused to read is not a row whose transitions it should
  // believe, so `unknown` keeps the empty list from `truth`'s default.
  const transitions = normaliseTransitions(row.transitions);
  if (state === "free_planned") {
    return truth(state, normaliseFreeReason(row.free_reason), transitions);
  }
  return truth(state, null, transitions);
}

/**
 * One room's row: the selected tenancy resolved against every month column.
 */
export function buildRoomCalendarCells({ room, tenancies, months }) {
  const tenancy = pickRelevantCalendarTenancyForRoom(room, tenancies);
  return (months || []).map((month) => ({
    monthKey: month.key,
    monthLabel: month.label,
    isCurrent: month.isCurrent,
    ...resolveCalendarMonthTruth({ tenancy, monthKey: month.key }),
  }));
}

/* ------------------------------------------------------------------ *
 * Aggregates
 * ------------------------------------------------------------------ */

export function formatChf(value) {
  return `CHF ${Math.round(Number(value || 0)).toLocaleString("de-CH")}`;
}

const isSecure = (s) => s === "secure";
const isCancellable = (s) => s === "cancellable" || s === "always_cancellable";
const isFree = (s) => s === "free_planned" || s === "free_unknown";

/**
 * What one month costs, as a share of the rent — or `null` for "we cannot say".
 *
 * `free_planned` is the only state whose price depends on more than itself, and
 * that is the whole point of S4.2b: a room standing empty because its contract
 * reached the end everyone signed up to is not a loss, while the same empty
 * room after a termination is exactly one. Before this, both were charged the
 * full rent and a healthy fixed-term contract read as the most expensive room
 * in the building.
 */
function monthLossShare(state, freeReason) {
  if (state === "free_planned") {
    if (freeReason === FREE_REASON_CONTRACT_END) return 0;
    if (freeReason === FREE_REASON_TERMINATION) return 1;
    return null; // unknown or missing → withhold, never guess
  }
  if (state === "free_unknown") return 1;
  if (isCancellable(state)) return 0.5;
  if (isSecure(state) || state === "reserved" || state === "notice_given") return 0;
  return null; // UNKNOWN_STATE and anything unrecognised
}

/**
 * The four figures a calendar prints per room.
 *
 * The counting rules are unchanged from before S4.1 — "kündbar" folds
 * `always_cancellable` in with `cancellable`, "frei" folds both free states
 * together, and the loss prices a free month at the full rent and a cancellable
 * one at half. `free_unknown` inherits exactly what the old unnamed "free"
 * contributed, so renaming the state does not quietly move a number.
 *
 * `unknown` fails CLOSED: it joins no count, and it makes the potential loss
 * refuse to answer at all. Pricing it at zero would understate the risk and
 * pricing it at a full month would invent one; the honest answer to "how much
 * might this room cost me" when a month is missing is that we do not know yet.
 *
 * S4.2b splits the loss `free_planned` used to carry unconditionally. THE
 * COUNTS ARE UNCHANGED — a month freed by a contract end is still a free month
 * and still says "Frei (geplant)" on screen; only its PRICE moved, from the
 * full rent to nothing. A `free_planned` month whose reason cannot be resolved
 * withholds the figure exactly as an unknown month does: the cell keeps its
 * word, but the sum refuses to pretend.
 */
export function buildCalendarAggregates(cells, monthlyPrice) {
  const monthly = Number(monthlyPrice || 0);
  let secure = 0;
  let cancellable = 0;
  let free = 0;
  let unknown = 0;
  let lost = 0;
  let unpriced = 0;

  let priced = 0;

  (cells || []).forEach(({ state, freeReason }) => {
    if (isSecure(state)) secure += 1;
    else if (isCancellable(state)) cancellable += 1;
    else if (isFree(state)) free += 1;
    else if (state === UNKNOWN_STATE) unknown += 1;

    const share = monthLossShare(state, freeReason);
    if (share === null) unpriced += 1;
    else {
      // R9: counted BEFORE the price is applied. A room with no rent still has
      // months the contract can price — they are worth CHF 0, which is an
      // ANSWER. Counting only the months that moved the sum would make every
      // priced zero look like a gap (see `pricedMonths` below).
      priced += 1;
      if (monthly) lost += monthly * share;
    }
  });

  const complete = unpriced === 0;
  return {
    secure,
    cancellable,
    free,
    unknown,
    // Months the loss could not price. Always >= `unknown`: it also covers a
    // `free_planned` month whose reason was missing or unrecognised, where the
    // CELL is fine and only the money is not. Exposed so a "—" can be explained
    // rather than merely observed.
    unpriced,
    complete,
    lostRevenue: complete ? lost : null,
    lostRevenueLabel: complete ? formatChf(lost) : INCOMPLETE_VALUE_LABEL,
    // ── R9 (P1-E): the part that IS known ──────────────────────────────────
    //
    // `lostRevenue` keeps its meaning exactly: the FULL total, or `null` when
    // one cannot be formed. R9 does not smuggle a smaller number into it —
    // that is the one change that would make a partial figure indistinguishable
    // from a complete one, which is precisely what must never happen.
    //
    // These two are additive and say something different. `pricedLostRevenue`
    // is the sum over the months the existing contract COULD price; it was
    // already being computed and then thrown away whenever anything was
    // unpriced. `pricedMonths` says how many months stand behind it.
    //
    // WHY THE COUNT AND NOT THE AMOUNT decides whether anything is known: a
    // legitimately priced month can be worth CHF 0 (a contract that reached its
    // own end costs nobody anything — §E.4.2). Reading "0" as "nothing known"
    // would turn a real answer into a gap. `pricedMonths === 0` is the only
    // honest test for "not one month could be priced".
    pricedLostRevenue: lost,
    pricedMonths: priced,
  };
}

/* ------------------------------------------------------------------ *
 * The potential-loss figure (R9 / P1-E)
 * ------------------------------------------------------------------ */

/** The three things a potential-loss figure can be. Never inferred from the amount. */
export const LOSS_COMPLETE = "complete";
export const LOSS_PARTIAL = "partial";
export const LOSS_NONE = "none";

/** The visible badge beside a partial amount. One word, both surfaces. */
export const PARTIAL_LABEL = "partiell";

/**
 * "2 Monate in 1 Zimmer nicht bepreisbar" — or without the rooms at room level.
 *
 * The room half is omitted rather than printed as "in 0 Zimmern": a room
 * aggregate has no room count, and inventing one would be the same class of
 * mistake as pricing an unknown month at zero.
 */
function describeLossGap(unpricedMonths, affectedRooms) {
  const months = `${unpricedMonths} ${unpricedMonths === 1 ? "Monat" : "Monate"}`;
  if (!affectedRooms) return `${months} nicht bepreisbar`;
  const rooms = `${affectedRooms} ${affectedRooms === 1 ? "Zimmer" : "Zimmern"}`;
  return `${months} in ${rooms} nicht bepreisbar`;
}

/**
 * What a potential-loss figure should read — for a room or for a unit.
 *
 * THE PROBLEM R9 SOLVES. Before this, one unpriceable month anywhere blanked
 * the whole figure: a unit that knew CHF 3'000 of loss across its other rooms
 * printed "—". The known money was computed and then discarded. That is a real
 * cost to hide, and hiding it does not make the gap any smaller.
 *
 * WHAT R9 REFUSES TO DO is the obvious shortcut — quietly putting the smaller
 * number where the full one used to be. A partial subtotal that looks exactly
 * like a total is worse than no number, because the reader cannot tell. So the
 * amount comes back with a `kind`, and every caller has to render that:
 *
 *   `complete`  every month priced. Identical to before R9, down to the label.
 *   `partial`   at least one month priced AND at least one not. Show the known
 *               amount, ALWAYS marked, always with the size of the gap.
 *   `none`      not a single month could be priced. Still "—". NEVER "CHF 0" —
 *               that would be the fail-closed rule (§E.4.2) undone in the last
 *               step, turning "we do not know" into "it cost nothing".
 *
 * `none` IS DECIDED BY THE COUNT, NEVER BY THE AMOUNT. A priced month may
 * legitimately be worth CHF 0 (a contract that reached its agreed end), so
 * "CHF 0 · partiell" is a correct and useful answer when something was priced.
 * Only `pricedMonths === 0` means nothing was.
 *
 * THE THREE TEXTS, and why there are three:
 *
 *   `gapText`         how big the gap is. VISIBLE beside a partial amount —
 *                     "CHF 9'000 · partiell" alone tells a manager something is
 *                     missing but not how much, which is not enough to act on.
 *   `srText`          what still needs announcing because the screen does not
 *                     show it. Empty for `partial` precisely BECAUSE `gapText`
 *                     is on screen; repeating it would read the sentence twice.
 *   `accessibleText`  the whole claim in one line, for `title`.
 */
export function potentialLossDisplay(aggregates) {
  const agg = aggregates || {};
  const complete = agg.complete !== false;
  const unpricedMonths = Number(agg.unpriced || 0);
  const affectedRooms = Number(agg.affectedRooms || 0);
  const pricedMonths = Number(agg.pricedMonths || 0);

  if (complete) {
    return {
      kind: LOSS_COMPLETE,
      amountLabel: agg.lostRevenueLabel,
      partial: false,
      gapText: "",
      // Nothing extra to say, so nothing extra is said — a complete figure must
      // stay byte-for-byte what it was before R9.
      accessibleText: "",
      srText: "",
    };
  }

  const gapText = describeLossGap(unpricedMonths, affectedRooms);

  if (pricedMonths === 0) {
    return {
      kind: LOSS_NONE,
      amountLabel: INCOMPLETE_VALUE_LABEL,
      partial: false,
      gapText,
      accessibleText: `${INCOMPLETE_HINT} – ${gapText}`,
      // "—" alone says nothing, so this one still needs announcing.
      srText: `${INCOMPLETE_HINT} – ${gapText}`,
    };
  }

  const amountLabel = formatChf(agg.pricedLostRevenue);
  return {
    kind: LOSS_PARTIAL,
    amountLabel,
    partial: true,
    gapText,
    accessibleText: `${amountLabel} – partieller Betrag, ${gapText}`,
    // DELIBERATELY EMPTY. A partial figure prints `gapText` on screen, so a
    // screen reader already meets the amount, the marking and the size of the
    // gap in the visible text. Repeating it in an `sr-only` span would read the
    // whole sentence twice. `accessibleText` stays for the `title`.
    //
    // WHICH SPAN CARRIES WHAT IS DECIDED HERE, not in the two components, so
    // the unit-detail calendar and the Co-Living matrix cannot drift into
    // announcing the same aggregate differently.
    srText: "",
  };
}

/**
 * A unit's totals.
 *
 * Incompleteness is contagious upward on purpose: a unit total built over one
 * room that could not be priced is not a smaller total, it is an unknown one.
 */
export function sumCalendarAggregates(list) {
  const rows = list || [];
  const total = rows.reduce(
    (acc, a) => ({
      secure: acc.secure + a.secure,
      cancellable: acc.cancellable + a.cancellable,
      free: acc.free + a.free,
      unknown: acc.unknown + (a.unknown || 0),
      unpriced: acc.unpriced + (a.unpriced || 0),
      // R9: the known part rolls up even when the total cannot.
      pricedLostRevenue: acc.pricedLostRevenue + (a.pricedLostRevenue || 0),
      pricedMonths: acc.pricedMonths + (a.pricedMonths || 0),
      // ROOMS, NOT MONTHS. A room with three unpriceable months is ONE affected
      // room; three rooms with one each are THREE. Counting cells here would
      // tell a manager to go looking in more places than exist.
      affectedRooms: acc.affectedRooms + ((a.unpriced || 0) > 0 ? 1 : 0),
    }),
    {
      secure: 0,
      cancellable: 0,
      free: 0,
      unknown: 0,
      unpriced: 0,
      pricedLostRevenue: 0,
      pricedMonths: 0,
      affectedRooms: 0,
    }
  );
  const complete = rows.every((a) => a.complete !== false);
  const lost = complete
    ? rows.reduce((sum, a) => sum + (a.lostRevenue || 0), 0)
    : null;
  return {
    ...total,
    complete,
    lostRevenue: lost,
    lostRevenueLabel: complete ? formatChf(lost) : INCOMPLETE_VALUE_LABEL,
  };
}
